import {useAccessToken, useUserType} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import React, {useMemo, useState} from "react"
import {Filter} from "../Filter/Filter"
import {useQueryState} from "use-location-state"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {createBulkAdjustments, findAdjustments, StatusType, StatusTypeValue} from "../../resources/adjustment"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faCircleNotch, faUpload} from "@fortawesome/free-solid-svg-icons"
import ReactTooltip from "react-tooltip"
import {useToasts} from "react-toast-notifications"
import classNames from "classnames"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {UnitAdminOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {AllowedAdjustmentsColumns} from "./Adjestments.types"
import AdjustmentsTable from "./AdjustmentsTable"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {Org, findOrgs} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {ErrorDocument} from "../../resources/common"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {Cents} from "../../resources/transaction"

interface AdjustmentsProps {
	token?: string
	limit?: number
	includedColumns: Array<AllowedAdjustmentsColumns>
	enableTitle?: boolean
	fullHeight?: boolean
	enableUpload?: boolean
	accountId?: string
	enableStatusFilter?: boolean
	enableOrgFilter?: boolean
	enableAmountFilter?: boolean
	enablePagination?: boolean
}

export function Adjustments({
	limit = 25,
	includedColumns,
	enableUpload,
	enableTitle,
	fullHeight,
	accountId,
	enableOrgFilter = false,
	enableAmountFilter = false,
}: AdjustmentsProps) {
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const prefix = "adj"
	const userType = useUserType()

	const [filteredAdjustmentStatuses, setFilteredAdjustmentsStatuses] = useQueryState<string[]>(
		`${prefix}-filter[status]`,
		Object.keys(StatusType)
	)
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findAdjustments(
				accessToken,
				offset,
				limit,
				accountId,
				filteredAdjustmentStatuses,
				undefined,
				filteredOrgs,
				fromAmount,
				toAmount
			),
		(x) => x.data.length,
		[filteredAdjustmentStatuses.join(","), refreshToken, filteredOrgs.join(","), fromAmount, toAmount]
	)

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={(value) => {
				setFromAmount(value)
				reset(limit)
			}}
			to={toAmount}
			setTo={(value) => {
				setToAmount(value)
				reset(limit)
			}}
		/>
	)

	const statusFilter = (
		<Filter
			title="Status"
			setStatuses={setFilteredAdjustmentsStatuses}
			statuses={filteredAdjustmentStatuses}
			onFilterFunc={() => reset(limit)}
			options={new Map(Object.keys(StatusType).map((t) => [t, StatusTypeValue(t)]))}
		/>
	)

	const [isFileLoading, setIsFileLoading] = useState(false)
	const {addToast} = useToasts()

	function onFileUploaded(e: React.ChangeEvent<HTMLInputElement>) {
		const files = e.target.files
		if (files) {
			const file = files[0]

			if (file) {
				setIsFileLoading(true)
				createBulkAdjustments(accessToken, file, "text/csv", file.name)
					.then((result) => {
						setIsFileLoading(false)
						result.match(
							(okResult) =>
								addToast(`Successfully added ${okResult.data.relationships.adjustments.data.length} new adjustments`, {
									appearance: "success",
								}),
							(errorResult) => {
								addToast(`${errorResult.errors[0].title} - ${errorResult.errors[0].detail}`, {appearance: "error"})
							}
						)
						refresh()
					})
					.catch(() => setIsFileLoading(false))
			}
		}
	}

	return (
		<DataTableCard className={"disputes-card"}>
			<UnitAdminOnly>
				{enableUpload ? (
					<div className="file column is-right is-flex is-align-items-right">
						<a className="is-flex is-align-items-right ml-1" data-tip="React-tooltip">
							<label className="file-label">
								<input
									className="file-input"
									disabled={isFileLoading}
									type="file"
									onChange={onFileUploaded}
									accept=".text,.csv"
								/>
								<span className={classNames("file-cta", isFileLoading && "file-input-disabled")}>
									<span className="file-icon">
										<FontAwesomeIcon icon={isFileLoading ? faCircleNotch : faUpload} spin={isFileLoading} />
									</span>
									<span className="file-label">Upload Adjustments CSV file</span>
								</span>
							</label>
							<ReactTooltip
								place="bottom"
								effect="solid"
								className="tooltip-info"
								border={true}
								borderColor="#C4C4C4"
								backgroundColor="#F5F5F5"
								textColor="black"
							>
								<span>
									<b>CSV file columns order:</b> [Direction (Credit/Debit)] [Account ID] [Counter Account ID]
									[Amount(dollars) xxx.xx] [Description] [Internal notes]
								</span>
							</ReactTooltip>
						</a>
					</div>
				) : null}
			</UnitAdminOnly>
			<DataTableActionHeader
				enableSticky={fullHeight}
				filters={[statusFilter, ...(enableOrgFilter ? [orgFilter] : []), ...(enableAmountFilter ? [amountFilter] : [])]}
				title={enableTitle ? "Adjustments" : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: adjustment}) => {
					return (
						<AdjustmentsTable
							adjustments={adjustment.data}
							meta={adjustment.meta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							isUsingPaging={true}
							include={adjustment.included}
							token={accessToken}
							fullHeight={fullHeight}
							includedColumns={includedColumns}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
